import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Star } from "lucide-react";

const EMPTY = { name: "", location: "", rating: 5, title: "", body: "", avatar_url: "", active: true, featured: false };

export default function AdminReviews() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { setItems((await api.get("/admin/reviews")).data); }
    catch { toast.error("Failed to load reviews"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/reviews", { ...editing, rating: parseInt(editing.rating, 10) || 5 });
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    await api.delete(`/admin/reviews/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-display text-4xl">Customer Reviews</h1>
        <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary" data-testid="add-review-btn">
          <Plus size={16} className="inline mr-1 -mt-0.5" /> New Review
        </button>
      </div>
      <p className="text-zinc-400 text-sm mb-6">
        Reviews rotate randomly on the homepage. Add real quotes from your customers, Whatnot buyers, or Discord community members.
      </p>

      {editing && (
        <form onSubmit={save} className="border border-white/10 rounded p-6 bg-[var(--cl-surface)] mb-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Customer Name *">
              <input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                     data-testid="review-name" className="input" placeholder="e.g. Marcus T." />
            </Field>
            <Field label="Location / Handle">
              <input value={editing.location || ""} onChange={(e) => setEditing({ ...editing, location: e.target.value })}
                     className="input" placeholder="e.g. Dallas, TX  or  @marcus_rips" />
            </Field>
            <Field label="Rating (1-5)">
              <select value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: e.target.value })} data-testid="review-rating" className="input">
                {[5,4,3,2,1].map((n) => <option key={n} value={n}>{n} ★</option>)}
              </select>
            </Field>
            <Field label="Avatar URL (optional)">
              <input value={editing.avatar_url || ""} onChange={(e) => setEditing({ ...editing, avatar_url: e.target.value })}
                     className="input" placeholder="https://…" />
            </Field>
            <Field label="Short Title (optional)" className="md:col-span-2">
              <input value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                     className="input" placeholder="e.g. Insane pull from the Feb case break!" />
            </Field>
            <Field label="Review Body *" className="md:col-span-2">
              <textarea required rows={4} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                        data-testid="review-body" className="input" placeholder="What did they love about their Card Lobster experience?" />
            </Field>
            <Field label="Status">
              <select value={editing.active ? "y" : "n"} onChange={(e) => setEditing({ ...editing, active: e.target.value === "y" })} className="input">
                <option value="y">Visible on homepage</option>
                <option value="n">Hidden</option>
              </select>
            </Field>
          </div>
          <div className="flex gap-3">
            <button type="submit" className="btn-primary" data-testid="review-save">Save</button>
            <button type="button" onClick={() => setEditing(null)} className="btn-outline">Cancel</button>
          </div>
        </form>
      )}

      <div className="space-y-3" data-testid="reviews-list">
        {items.length === 0 && <div className="text-zinc-500">No reviews yet. Add your first one above.</div>}
        {items.map((r) => (
          <div key={r.id} className="flex items-start gap-4 border border-white/10 rounded p-4 bg-[var(--cl-surface)]">
            {r.avatar_url ? (
              <img src={r.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-[var(--cl-primary)]/20 text-[var(--cl-primary)] flex items-center justify-center font-display text-xl flex-shrink-0">
                {r.name?.[0]?.toUpperCase() || "?"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium">{r.name}</span>
                {r.location && <span className="text-xs text-zinc-500">· {r.location}</span>}
                <span className="ml-2 flex items-center gap-0.5 text-[var(--cl-gold)]">
                  {Array.from({length: r.rating}).map((_, i) => <Star key={i} size={12} fill="currentColor" />)}
                </span>
              </div>
              {r.title && <div className="font-display text-lg mt-1">{r.title}</div>}
              <p className="text-sm text-zinc-400 mt-1 line-clamp-3">{r.body}</p>
            </div>
            <span className={`text-[10px] px-2 py-1 rounded ${r.active ? "border border-green-500/50 text-green-400" : "border border-white/15 text-zinc-500"}`}>
              {r.active ? "VISIBLE" : "HIDDEN"}
            </span>
            <button onClick={() => setEditing(r)} className="text-xs btn-outline px-3 py-1">Edit</button>
            <button onClick={() => del(r.id)} className="p-2 hover:text-[var(--cl-primary)]" data-testid={`del-review-${r.id}`}>
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
      <style>{`.input { width: 100%; background: #000; border: 1px solid rgba(255,255,255,0.1); border-radius: 6px; padding: 8px 12px; color: white; font-size: 0.875rem; }`}</style>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-label text-zinc-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
